import { useEffect, useMemo, useRef } from "react";
import mapboxgl from "mapbox-gl";
import iconGatewayPin from "../../img/map-gateway-pin.svg";
import {
	MapHelperInstance,
	useListenerSetupMapEffect,
	useStyleDependantMapEffect,
} from "../../helper/map-helper";

export interface MapboxInjectablePositionGatewayParams {
	mapInstance: MapHelperInstance;
	disabled?: boolean;
	position: [number, number] | null;
	setPosition: (position: [number, number] | null) => void;
}

export default function useMapboxInjectablePositionGateway(
	params: MapboxInjectablePositionGatewayParams,
) {
	const markerPosition = useMemo(() => {
		const markerElement = document.createElement("img");
		markerElement.src = iconGatewayPin;
		markerElement.style.pointerEvents = "none";
		return new mapboxgl.Marker({
			element: markerElement,
			anchor: "bottom",
		});
	}, []);
	const markerPositionIsOnMap = useRef(false);

	//Set the cursor to a crosshair while we are enabled
	useStyleDependantMapEffect(
		params.mapInstance,
		(map, box) => {
			if (!params.disabled) {
				map.getCanvas().style.cursor = "crosshair";
			} else {
				map.getCanvas().style.cursor = "";
			}
		},
		[params.disabled],
	);

	useListenerSetupMapEffect(
		params.mapInstance.map,
		(map, box) => {
			if (params.disabled) {
				return;
			}

			box.on("mousedown", (event) => {
				params.setPosition([event.lngLat.lng, event.lngLat.lat]);
			});
		},
		[params.disabled, params.setPosition],
	);

	useEffect(() => {
		if (!params.mapInstance.map) {
			return;
		}

		if (params.position && !params.disabled) {
			markerPosition.setLngLat(params.position);

			if (!markerPositionIsOnMap.current) {
				markerPosition.addTo(params.mapInstance.map);
				markerPositionIsOnMap.current = true;
			}
		} else {
			if (markerPositionIsOnMap.current) {
				markerPosition.remove();
				markerPositionIsOnMap.current = false;
			}
		}
	}, [
		params.mapInstance.map,
		params.disabled,
		params.position,
		markerPosition,
	]);
}
